import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useSession } from "@/app/session";
import { Alert, Button, Spinner } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { AuthLayout } from "./auth/AuthLayout";

interface Info {
  kind: "workspace" | "board";
  valid: boolean;
  email: string | null;
  role: string;
  workspaceName?: string;
  boardTitle?: string | null;
}

export default function Invite() {
  const t = useT();
  const { token = "" } = useParams();
  const { me, loading } = useSession();
  const nav = useNavigate();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Info>(`/api/invites/${token}`)
      .then(setInfo)
      .catch(() => setError(t.inviteExpired));
  }, [token]);

  async function accept() {
    setBusy(true);
    try {
      const r = await api<{ kind: string; boardId: string }>(`/api/invites/${token}/accept`, {
        method: "POST",
      });
      nav(`/b/${r.boardId}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError && e.code === "email_mismatch" ? t.emailMismatch : t.inviteExpired);
      setBusy(false);
    }
  }

  const what =
    info?.kind === "workspace"
      ? `el equipo de ${info.workspaceName}`
      : `el tablero "${info?.boardTitle ?? ""}"`;
  const next = `/invitacion/${token}`;
  return (
    <AuthLayout title={t.acceptInvite}>
      {error ? <Alert kind="error">{error}</Alert> : null}
      {!info && !error ? <Spinner /> : null}
      {info && !error ? (
        <>
          <p className="mb-6 text-muted">
            {what} · {t.roles[info.role]}
          </p>
          {!info.valid ? <Alert kind="error">{t.inviteExpired}</Alert> : null}
          {info.valid && !loading && me ? (
            <Button className="w-full" onClick={accept} loading={busy}>
              {t.acceptInvite}
            </Button>
          ) : null}
          {info.valid && !loading && !me ? (
            <div className="flex flex-col gap-2">
              <Link to={`/login?next=${encodeURIComponent(next)}`}>
                <Button className="w-full">{t.signIn}</Button>
              </Link>
              <Link
                to={`/registro?next=${encodeURIComponent(next)}`}
                className="text-center text-sm text-muted hover:text-text"
              >
                {t.createAccount}
              </Link>
            </div>
          ) : null}
        </>
      ) : null}
    </AuthLayout>
  );
}
